import { type FormEvent, useState } from 'react';
import CoinStack from '@/assets/icons/CoinStack.svg';
import type { PaymentOptionsData } from '../MyAccount.types';
import { postAccountAction } from '../shared/postAccountAction';

interface PaymentOptionsPageProps {
  data: PaymentOptionsData;
}

export const PaymentOptionsPage = ({ data }: PaymentOptionsPageProps) => {
  const [loyaltyNumber, setLoyaltyNumber] = useState(data.loyaltyCard?.loyaltyCardNumber ?? '');
  const [isLinked, setIsLinked] = useState(!!data.loyaltyCard?.loyaltyCardNumber);
  const [points, setPoints] = useState(data.loyaltyCard?.pointsBalance ?? null);
  const [newLoyaltyInput, setNewLoyaltyInput] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [message, setMessage] = useState<{ text: string; isError: boolean } | null>(null);

  const postAction = async (url: string, body: Record<string, unknown>) => {
    setIsPending(true);
    try {
      return await postAccountAction(url, body);
    } finally {
      setIsPending(false);
    }
  };

  const handleLinkLoyalty = async (e: FormEvent) => {
    e.preventDefault();
    setMessage(null);
    const result = await postAction(data.endpoints.addLoyalty, { loyaltyCardNumber: newLoyaltyInput });
    if (result.success) {
      setIsLinked(true);
      setLoyaltyNumber(newLoyaltyInput);
      setPoints(result.pointsBalance ?? null);
      setNewLoyaltyInput('');
    }
    setMessage({
      text: result.message ?? (result.success ? 'Loyalty card linked' : 'Failed to link loyalty card'),
      isError: !result.success,
    });
  };

  const handleUnlinkLoyalty = async () => {
    setMessage(null);
    const result = await postAction(data.endpoints.removeLoyalty, {});
    if (result.success) {
      setIsLinked(false);
      setLoyaltyNumber('');
      setPoints(null);
    }
  };

  return (
    <>
      {/* Loyalty card section */}
      {data.loyaltyCardEnabled && (
        <div className="pay-section">
          <div className="ma-head"><h3>Link your loyalty card</h3></div>
          <p className="ma-hint">Earn points every time you shop at Eason — just link your card number to your account.</p>
          <div className="pay-loyalty">
            {isLinked ? (
              <>
                {points !== null && (
                  <div className="pay-points">
                    <CoinStack aria-hidden="true" />
                    <strong>{points.toLocaleString()}</strong> points
                  </div>
                )}
                <div className="ma-field">
                  <label className="ma-label">Linked card number</label>
                  <input className="ma-input" type="text" value={loyaltyNumber} readOnly />
                </div>
                <button type="button" className="ma-btn-secondary ma-btn-destructive" style={{ marginTop: '14px' }} onClick={handleUnlinkLoyalty} disabled={isPending}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}><path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 0 1 0 10h-2"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                  Unlink card
                </button>
                <div className="pay-linkrow">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}><path d="M20 6 9 17l-5-5"/></svg>
                  This card is linked to your account.
                </div>
              </>
            ) : (
              <form onSubmit={handleLinkLoyalty}>
                <div className="ma-field">
                  <label className="ma-label" htmlFor="loyalty-input">Loyalty card number</label>
                  <div className="pay-field-row">
                    <input
                      id="loyalty-input"
                      className={`ma-input${message?.isError ? ' has-error' : ''}`}
                      type="text"
                      value={newLoyaltyInput}
                      onChange={e => { setNewLoyaltyInput(e.target.value); if (message?.isError) setMessage(null); }}
                      placeholder="Enter your card number"
                      required
                      style={{ flex: '1 1 320px' }}
                    />
                    <button type="submit" className="ma-btn-primary" style={{ marginTop: 0 }} disabled={isPending}>
                      Link card
                    </button>
                  </div>
                  {message?.isError && <span className="ma-field-error">{message.text}</span>}
                </div>
              </form>
            )}
            {message && !message.isError && (
              <p className="ma-success">{message.text}</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};
